import { useEffect, useRef, useState } from "react";
import type { DashboardInstance } from "../../../pages/dashboard/mock";
import { StatusPill } from "../StatusPill";
import styles from "./SwitcherDropdown.module.css";

type SwitcherDropdownProps = {
  instances: DashboardInstance[];
  currentId: string;
  onSelect: (id: string) => void;
  onLaunchNew?: () => void;
};

const REGION_LABELS: Record<NonNullable<DashboardInstance["region"]>, string> = {
  "ap-southeast-2": "Sydney",
  "us-east-1": "Virginia",
  "eu-west-1": "Dublin",
};

const labelForRegion = (region: DashboardInstance["region"]): string =>
  region === undefined ? "—" : REGION_LABELS[region];

const STATUS_LABEL: Record<DashboardInstance["status"], string> = {
  running: "Running",
  provisioning: "Provisioning",
};

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
};

const ChevronIcon = ({ className }: { className?: string }): JSX.Element => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3.5 5.25L7 8.75l3.5-3.5" />
  </svg>
);

const PlusIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M7 3v8M3 7h8" />
  </svg>
);

export const SwitcherDropdown = ({
  instances,
  currentId,
  onSelect,
  onLaunchNew,
}: SwitcherDropdownProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const current = instances.find((i) => i.id === currentId) ?? instances[0];

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!current) {
    return <div className={styles.root} />;
  }

  const handleSelect = (id: string): void => {
    onSelect(id);
    setOpen(false);
  };

  const handleLaunch = (): void => {
    if (onLaunchNew) onLaunchNew();
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        data-testid="switcher-trigger"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={styles.monogram} aria-hidden>
          {getInitials(current.name)}
        </span>
        <span className={styles.textBlock}>
          <span className={styles.name}>{current.name}</span>
          <span className={styles.meta}>
            {labelForRegion(current.region)}
            <span className={styles.metaSep}>·</span>
            <span
              className={`${styles.statusDot} ${styles[current.status]}`}
              aria-hidden
            />
            {STATUS_LABEL[current.status]}
          </span>
        </span>
        <ChevronIcon
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
        />
      </button>

      <div
        className={`${styles.panel} ${open ? styles.panelOpen : ""}`}
        role="listbox"
        aria-label="Your instances"
        data-testid="switcher-panel"
      >
        <div className={styles.sectionHeader}>Your instances</div>
        <div className={styles.list}>
          {instances.map((inst) => {
            const selected = inst.id === currentId;
            return (
              <button
                key={inst.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={`${styles.row} ${selected ? styles.rowSelected : ""}`}
                onClick={() => handleSelect(inst.id)}
              >
                <span className={styles.monogram} aria-hidden>
                  {getInitials(inst.name)}
                </span>
                <span className={styles.textBlock}>
                  <span className={styles.name}>{inst.name}</span>
                  <span className={styles.meta}>
                    {labelForRegion(inst.region)}
                    <span className={styles.metaSep}>·</span>
                    {inst.status === "running"
                      ? `${inst.uptimeDays}d uptime`
                      : "Spinning up"}
                  </span>
                </span>
                <span className={styles.rowRight}>
                  {selected ? (
                    <StatusPill variant="active" />
                  ) : inst.status === "provisioning" ? (
                    <StatusPill variant="provisioning" />
                  ) : (
                    <span className={styles.rowStatus}>
                      <span
                        className={`${styles.statusDot} ${styles[inst.status]}`}
                        aria-hidden
                      />
                      {STATUS_LABEL[inst.status]}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <div className={styles.divider}>
          <button
            type="button"
            className={styles.launchRow}
            onClick={handleLaunch}
          >
            <span
              className={`${styles.monogram} ${styles.monogramDashed}`}
              aria-hidden
            >
              <PlusIcon />
            </span>
            <span className={styles.textBlock}>
              <span className={styles.launchText}>Launch new instance</span>
              <span className={styles.launchSub}>~3 minutes to ready</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
